/** True when running in a browser (false during SSR: Nuxt, Next.js, Angular Universal…). */
export const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * HTMLElement in the browser, an empty class on the server, so that importing
 * Hanami UI never crashes a server-side render.
 */
export const BaseElement = (typeof HTMLElement !== 'undefined' ? HTMLElement : class {}) as typeof HTMLElement;

/** Registers a custom element once (safe when several bundles import Hanami UI). */
export function define(tag: string, ctor: CustomElementConstructor): void {
  if (typeof customElements === 'undefined') return;
  if (!customElements.get(tag)) customElements.define(tag, ctor);
}

const sheets = new Map<string, CSSStyleSheet>();
const supportsAdopted = isBrowser && 'adoptedStyleSheets' in Document.prototype && 'replaceSync' in CSSStyleSheet.prototype;

/** Attaches an open shadow root with the given styles (shared constructable sheet) and markup. */
export function createShadow(host: HTMLElement, css: string, html: string, options: Partial<ShadowRootInit> = {}): ShadowRoot {
  const root = host.shadowRoot ?? host.attachShadow({ mode: 'open', ...options });
  if (supportsAdopted) {
    let sheet = sheets.get(css);
    if (!sheet) {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      sheets.set(css, sheet);
    }
    root.adoptedStyleSheets = [sheet];
    root.innerHTML = html;
  } else {
    root.innerHTML = `<style>${css}</style>${html}`;
  }
  return root;
}

let uid = 0;
/** Unique, stable-per-call id with a readable prefix. */
export function uniqueId(prefix = 'hn'): string {
  uid += 1;
  return `${prefix}-${uid.toString(36)}`;
}

/** Base class shared by every Hanami UI element. */
export abstract class HanamiElement extends BaseElement {
  /** Dispatches a bubbling, composed CustomEvent. Returns false if a listener called preventDefault(). */
  protected emit<T>(type: string, detail?: T, cancelable = false): boolean {
    return this.dispatchEvent(new CustomEvent<T>(type, { detail, bubbles: true, composed: true, cancelable }));
  }

  /**
   * Boolean attribute that also treats the string "false" as false, because some
   * frameworks (React 18, attribute bindings) serialise `false` instead of removing it.
   */
  protected getBool(name: string): boolean {
    const value = this.getAttribute(name);
    return value !== null && value !== 'false';
  }

  protected setBool(name: string, value: boolean): void {
    this.toggleAttribute(name, Boolean(value));
  }

  protected getNumber(name: string, fallback: number): number {
    const value = this.getAttribute(name);
    if (value === null || value.trim() === '') return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  /**
   * A framework may set a property before the element is upgraded; the value then
   * shadows our accessor. Re-apply it through the setter once we are defined.
   */
  protected upgradeProperties(...props: string[]): void {
    for (const prop of props) {
      if (Object.prototype.hasOwnProperty.call(this, prop)) {
        const self = this as unknown as Record<string, unknown>;
        const value = self[prop];
        delete self[prop];
        self[prop] = value;
      }
    }
  }
}

export const prefersReducedMotion = (): boolean =>
  isBrowser && typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Resolves after the element's current CSS animations finish (or after `timeout`). */
export function animationsDone(el: Element, timeout = 600): Promise<void> {
  return new Promise(resolve => {
    const running = typeof el.getAnimations === 'function' ? el.getAnimations({ subtree: true }) : [];
    if (!running.length) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, timeout);
    Promise.allSettled(running.map(a => a.finished)).then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}
